import { MobileUserOrderModal } from 'components/MobileUserOrderModalV3'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from 'store'
import {
  CoinData,
  CoinSize,
  CoinIcon,
  CoinName,
  StyledColumn,
  SymbolLink,
  TriggerDone,
  StyledEndItem,
} from './styles'
import {
  orderModalAtom,
  OrderModalType,
} from 'components/MobileUserOrderModalV3'
import { DateFormatter } from 'utils/datetime'
import { useSpotSymbols } from 'store/spot/hooks'
import { findSymbolById } from 'utils/symbols'
import { ReactComponent as ArrowIconSvg } from 'assets/orders/symbol_arrow.svg'
import { useMarketNavigate } from 'hooks/useMarketNavigate'
import { SpotOrderRecord } from 'api/v1/spot'
import { TrendColorizer } from 'components/TrendColorizer'
import { OrderDirection } from 'store/spot/types'
import { toFixed, isZero } from 'utils/number'
import { OrderStatus } from 'components/OrderStatus'
import { TableList } from 'components/TableList'
import useWindow from 'hooks/useWindow'
import { useSpotCurrencies } from 'store/market/hooks'
import { Flex } from 'styles'
import { calcOrderFilledSize } from 'hooks/useSizeCalculations'
import { useSetAtom } from 'jotai'
import { updateModal } from 'store/app/actions/modalActions'
import { SpotOrderStatus } from 'api/v1/spot'
import { PerpOrderRecord } from 'api/v1/perp'
import { StringFormatter } from 'utils/StringFormatter'

interface IProps {
  list?: any
  isLastPage: boolean
  onScrollFetch: () => any
}

export function customOrderHistoryRowStyle(
  row: SpotOrderRecord | PerpOrderRecord,
) {
  const keys = [SpotOrderStatus.FULLY_CANCELLED, SpotOrderStatus.FAILED]
  if (keys.indexOf(row.status) > -1) {
    return {
      opacity: 0.6,
    }
  }
  return {}
}

export const SpotOrderHistory = ({
  list,
  isLastPage,
  onScrollFetch,
}: IProps) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const spotSymbols = useSpotSymbols()
  const { navigateToSpot } = useMarketNavigate()
  const { isMobile } = useWindow()
  const tokenList = useSpotCurrencies()
  const setSelectedOrder = useSetAtom(orderModalAtom)

  const getSymbolInfo = (symbolId: number) => {
    const symbol = findSymbolById(spotSymbols, symbolId)
    const {
      baseCurrency = '',
      quoteCurrency = '',
      baseScale = 0,
      quoteScale = 0,
    } = symbol ?? {}

    return {
      baseCurrency,
      quoteCurrency,
      baseScale,
      quoteScale,
    }
  }

  const getFilledSizeQuantity = (row: SpotOrderRecord) => {
    const { symbolId, quantity = 0, unfilledQuantity = 0 } = row
    const symbol = getSymbolInfo(symbolId)
    const { baseScale } = symbol
    const filledSize = calcOrderFilledSize(quantity, unfilledQuantity)
    const fixedQuantity = toFixed(quantity, baseScale)
    const fixedFilledSize = toFixed(filledSize, baseScale)
    return {
      fixedFilledSize,
      fixedQuantity,
    }
  }

  const columns = [
    {
      key: 'date',
      title: 'Date',
      render: (row: SpotOrderRecord) => {
        const { createdAt } = row
        const datetime = DateFormatter.toISODateTime(
          createdAt,
          'YYYY-MM-DD HH:mm',
        )
        return <span style={{ color: '#fff' }}>{datetime}</span>
      },
      width: '14%',
    },

    {
      key: 'symbol',
      title: 'Pair',
      // t('user-order-table-header-market'),
      render: (row: SpotOrderRecord) => {
        const { symbolId } = row
        const symbol = getSymbolInfo(symbolId)
        const { baseCurrency, quoteCurrency } = symbol ?? {}

        return (
          <SymbolLink
            sx={{ cursor: 'pointer' }}
            onClick={() => navigateToSpot(baseCurrency, quoteCurrency)}
          >
            {baseCurrency}/{quoteCurrency}
            <ArrowIconSvg />
          </SymbolLink>
        )
      },
      width: '12%',
    },
    {
      key: 'type',
      title: t('user-order-table-header-type'),
      render: (row: SpotOrderRecord) => {
        const { type } = row
        return StringFormatter.startCase(type)
      },
      width: '8%',
    },
    {
      key: 'side',
      title: 'Side',
      render: (row: SpotOrderRecord) => {
        const { direction } = row
        return (
          <TrendColorizer
            trend={direction === OrderDirection.LONG}
            value={
              direction === OrderDirection.LONG
                ? t('user-order-table-content-buy')
                : t('user-order-table-content-sell')
            }
          />
        )
      },
      width: '6%',
    },
    {
      key: 'filled',
      title: t('history-orders-table-filled'),
      render: (row: SpotOrderRecord) => {
        const { symbolId } = row
        const { baseCurrency } = getSymbolInfo(symbolId)
        const { fixedFilledSize, fixedQuantity } = getFilledSizeQuantity(row)
        return (
          <>
            <span>{fixedFilledSize}</span>
            <span>
              /{fixedQuantity} {baseCurrency}
            </span>
          </>
        )
      },
      width: '14%',
    },
    {
      key: 'avg-price',
      title: t('history-orders-table-avg-price'),
      render: (row: SpotOrderRecord) => {
        const { symbolId, fillPrice } = row
        const { quoteScale, quoteCurrency } = getSymbolInfo(symbolId)
        return (
          <>
            {fillPrice > 0
              ? toFixed(fillPrice, quoteScale) + ' ' + quoteCurrency
              : '-'}
          </>
        )
      },
      width: '12%',
    },
    {
      key: 'price',
      title: t('user-order-table-header-price'),
      render: (row: SpotOrderRecord) => {
        const { symbolId, price } = row
        const { quoteScale, quoteCurrency } = getSymbolInfo(symbolId)
        const fixedPrice = toFixed(price, quoteScale)
        return (
          <>
            {fixedPrice} {quoteCurrency}
          </>
        )
      },
      width: '14%',
    },
    {
      key: 'Trigger Conditions',
      title: t('user-order-table-header-trigger'),
      render: (row: SpotOrderRecord) => {
        const {
          triggerOn,
          triggerDirection = OrderDirection.LONG,
          status,
        } = row
        return (
          <>
            {isZero(triggerOn) ? (
              '-'
            ) : (
              <>
                Last Price{' '}
                {triggerDirection === OrderDirection.LONG ? '>=' : '<='}{' '}
                {triggerOn}
                {status === SpotOrderStatus.PENDING && <TriggerDone />}
              </>
            )}
          </>
        )
      },
      width: '14%',
    },
    {
      key: 'status',
      title: 'Status',
      render: (row: SpotOrderRecord) => {
        const { status } = row
        return <OrderStatus status={status} />
      },
      width: '10%',
    },
  ]

  const mobileColumns = [
    {
      key: 'mobile_symbol',
      title: `${t('user-order-table-header-size')}/${t(
        'user-order-table-header-market',
      )}`,
      render: (row: SpotOrderRecord) => {
        const { symbolId, direction, symbol: symbolName } = row
        const symbol = getSymbolInfo(symbolId)
        const { baseCurrency } = symbol
        const token = tokenList.find((t) => baseCurrency === t.name)
        const { tokenIconUrl = '' } = token ?? {}
        const { fixedQuantity } = getFilledSizeQuantity(row)
        return (
          <Flex>
            <CoinIcon>
              {tokenIconUrl && <img src={tokenIconUrl} alt={baseCurrency} />}
            </CoinIcon>
            <CoinData>
              <CoinSize
                className={direction === OrderDirection.LONG ? 'buy' : 'sell'}
              >
                {fixedQuantity}
              </CoinSize>
              <CoinName>{symbolName}</CoinName>
            </CoinData>
          </Flex>
        )
      },
    },
    {
      key: 'mobile_price',
      titleRender: () => {
        return (
          <StyledColumn style={{ alignItems: 'flex-start' }}>
            {t('user-order-table-header-price')}/
            {t('user-order-table-header-amount')}
          </StyledColumn>
        )
      },
      render: (row: SpotOrderRecord) => {
        const { symbolId, price } = row
        const { quoteScale, baseCurrency } = getSymbolInfo(symbolId)
        const fixedPrice = toFixed(price, quoteScale)
        const { fixedQuantity } = getFilledSizeQuantity(row)
        return (
          <StyledColumn style={{ alignItems: 'flex-start' }}>
            <span className="title">{fixedPrice}</span>
            <span className="sub">
              {fixedQuantity} {baseCurrency}
            </span>
          </StyledColumn>
        )
      },
    },
    {
      key: 'mobile_filled',
      titleRender: () => {
        return (
          <StyledColumn style={{ alignItems: 'flex-end' }}>
            {t('user-order-table-header-filled')}/
            {t('user-order-table-header-size')}
          </StyledColumn>
        )
      },
      render: (row: SpotOrderRecord) => {
        const { fixedQuantity, fixedFilledSize } = getFilledSizeQuantity(row)
        return (
          <StyledColumn style={{ alignItems: 'flex-end' }}>
            <span className="title">{fixedFilledSize}</span>
            <span className="sub">{fixedQuantity}</span>
          </StyledColumn>
        )
      },
    },
  ]

  return (
    <>
      <TableList
        hasMore={!isLastPage}
        tableBodyStyle={{ height: '460px' }}
        onScrollEnd={() => {
          if (isLastPage) return
          onScrollFetch()
        }}
        rowStyle={customOrderHistoryRowStyle}
        mobileRowClick={(row) => {
          if (isMobile) {
            setSelectedOrder({
              orderType: OrderModalType.SpotOrderHistory,
              data: row,
            })
            dispatch(updateModal({ modal: 'mobileUserOrder', open: true }))
          }
        }}
        columns={!isMobile ? columns : mobileColumns}
        dataSource={list}
      />

      <MobileUserOrderModal />
    </>
  )
}
